import { ThunkDispatch } from "core-integration/src/store";
import { getFutureEvents } from "core-integration/src/store/events/reducers";
import { getRules } from "core-integration/src/store/game/reducers";
import { updateH2HLeague } from "core-integration/src/store/leagues/thunks";
import {
  IPrivateLeagueAdmin,
  IUpdateH2HLeagueData,
  LeagueCodePrivacy,
} from "core-integration/src/store/leagues/types";
import range from "lodash/range";
import { Button } from "plos/src/components/buttons/Button";
import { Checkbox, FormSelect, FormTextField } from "plos/src/components/forms";
import Subheading from "plos/src/components/Subheading";
import { useLocalizedNavigate } from "plos/src/hooks/useLocalizedNavigate";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  formElementWrapper,
  formItem,
  leagueGridLayout,
} from "../../LeaguesAndCups/leaguesAndCups.css";

interface H2HDetailsProps {
  league: IPrivateLeagueAdmin;
  started: boolean;
}
interface IState {
  codePrivacy: LeagueCodePrivacy;
  closed: boolean;
  koRounds: number;
  maxEntries: number;
  name: string;
  startEvent: number;
}

const initializeState = (league: IPrivateLeagueAdmin): IState => ({
  closed: league.closed,
  codePrivacy: league.code_privacy,
  koRounds: league.ko_rounds || 0,
  maxEntries: league.max_entries || 0,
  name: league.name,
  startEvent: league.start_event,
});

const H2HDetails = ({ league, started }: H2HDetailsProps) => {
  const { t } = useTranslation("leaguesAndCups");
  const [state, setState] = useState<IState>(initializeState(league));

  useEffect(() => {
    if (league) {
      setState(initializeState(league));
    }
  }, [league]);

  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useLocalizedNavigate();
  const events = useSelector(getFutureEvents);
  const rules = useSelector(getRules);

  const maximumKoRounds = () => {
    const remainingEvents = events.length;
    if (remainingEvents <= 1) {
      return 0;
    }
    return Math.min(
      rules.league_max_ko_rounds_private_h2h,
      remainingEvents - 1,
    );
  };

  const minimumSize = (koRounds: number) =>
    state.koRounds ? Math.pow(2, koRounds) : 2;
  const validStartingEvents = (koRounds: number) =>
    koRounds ? events.slice(0, -koRounds) : events;

  const apiDataFromState = (): IUpdateH2HLeagueData => ({
    admin_entry: league.admin_entry,
    closed: state.closed,
    code_privacy: state.codePrivacy,
    ko_rounds: state.koRounds || null,
    max_entries: state.maxEntries || null,
    name: state.name,
    start_event: state.startEvent,
  });

  const handleNameChange = (val: string) => setState({ ...state, name: val });

  const handleCodePrivacyChange = (
    e: React.SyntheticEvent<HTMLSelectElement>,
  ) =>
    setState({
      ...state,
      codePrivacy: e.currentTarget.value as LeagueCodePrivacy,
    });

  const handleStartEventChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, startEvent: parseInt(e.currentTarget.value, 10) });

  const handleClosedChange = (val: boolean) => {
    setState({ ...state, closed: val });
  };

  const handleMaxEntriesChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, maxEntries: parseInt(e.currentTarget.value, 10) });

  const handleKoRoundsChange = (e: React.SyntheticEvent<HTMLSelectElement>) => {
    const koRounds = parseInt(e.currentTarget.value, 10);
    const minSize = minimumSize(koRounds);
    const events = validStartingEvents(koRounds);
    setState({
      ...state,
      koRounds,
      maxEntries: state.maxEntries ? Math.max(minSize, state.maxEntries) : 0,
      startEvent: Math.min(events[events.length - 1].id, state.startEvent),
    });
  };

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    await dispatch(updateH2HLeague(league.id, apiDataFromState()));
    navigate("/leagues");
  };

  const privacyOptions: { [key: string]: string } = {
    a: "All game players",
    l: "League members",
    p: "League administrator",
  };

  const disabled = !state.codePrivacy || !state.name || !state.startEvent;

  return (
    <>
      <Subheading>{t("leagueDetails", "League Details")}</Subheading>
      <form onSubmit={handleSubmit}>
        <div className={leagueGridLayout}>
          <div className={formElementWrapper}>
            <FormTextField
              label={t("leagueName", "League Name")}
              description={t("characterLimit", "Maximum 30 characters")}
              value={state.name}
              onChange={handleNameChange}
              placeholder={t("enterNameHere", "Enter Name Here")}
              maxLength={30}
              isDisabled={league.name_change_blocked}
            />
            <p>
              {league.name_change_blocked
                ? t(
                    "pendingModeration",
                    "Your requested league name is currently pending moderation and can't be edited at this time.",
                  )
                : t(
                    "appropriateNameGuidance",
                    "Please think carefully before entering your league name. Leagues with names that are deemed inappropriate or offensive may result in your account being deleted. Please refer to the Terms & Conditions of entry for more information.",
                  )}
            </p>
          </div>
          {!started && (
            <>
              <div className={formItem}>
                <FormSelect
                  id="league-code-privacy"
                  description={t(
                    "leagueCodePrivacy.description",
                    "This sets who the league code is viewable by",
                  )}
                  label={t("leagueCodePrivacy.label", "League code privacy")}
                  value={state.codePrivacy}
                  onChange={handleCodePrivacyChange}
                >
                  {Object.entries(privacyOptions).map(([id, label]) => (
                    <option key={id} value={id}>
                      {label}
                    </option>
                  ))}
                </FormSelect>
              </div>
              <div className={formItem}>
                <FormSelect
                  id="scoring-starts"
                  description={t(
                    "scoringStarts.description",
                    "Any changes will be reflected after the next points update",
                  )}
                  label={t("scoringStarts.label", "Scoring starts")}
                  value={state.startEvent}
                  onChange={handleStartEventChange}
                >
                  {validStartingEvents(state.koRounds).map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </FormSelect>
              </div>
              <div className={formItem}>
                <FormSelect
                  id="maximum-size"
                  value={state.maxEntries}
                  onChange={handleMaxEntriesChange}
                  label={t("maximumSize.label", "Maximum size")}
                >
                  <option value={0}>
                    {t("maximumSize.unlimited", "Unlimited")}
                  </option>
                  {range(
                    minimumSize(state.koRounds),
                    rules.league_max_size_private_h2h + 1,
                  ).map((num) => (
                    <option value={num} key={num}>
                      {num}
                    </option>
                  ))}
                </FormSelect>
              </div>
              {maximumKoRounds() > 0 && (
                <div className={formItem}>
                  <FormSelect
                    id="league-ko-rounds"
                    value={state.koRounds}
                    onChange={handleKoRoundsChange}
                    label={t("knockoutRounds.label", "Knockout rounds")}
                  >
                    <option value={0} aria-selected={!state.koRounds}>
                      {t("knockoutRounds.none", "None")}
                    </option>
                    {range(1, maximumKoRounds() + 1).map((num) => (
                      <option value={num} key={num}>
                        {num}
                      </option>
                    ))}
                  </FormSelect>
                </div>
              )}
              <div className={formItem}>
                <Checkbox
                  isSelected={state.closed}
                  onChange={handleClosedChange}
                >
                  {t("closeLeagueToNewEntries", "Close league to new entries")}
                </Checkbox>
              </div>
            </>
          )}
          <div className={formItem}>
            <Button type="submit" isDisabled={disabled}>
              {t("updateLeague", "Update League")}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};
export default H2HDetails;
